import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  BaseMapProvider,
  EXTERNAL_MAP_LAYERS_SERVICE,
  ExternalMapLayer,
  InventaMapType,
} from '../../../services/external-layers/external-map-layers.service';
import { FeatureLayerDetailsService } from '../../../services/map/feature-layer-details.service';
import { MapService } from '../../../services/map/map.service';

type InventaLayerId =
  | 'speciesIcon'
  | 'lightCondition'
  | 'tracks'
  | 'trackArrows'
  | 'markers'
  | 'clustering';

interface UnifiedBaseTile {
  key: string;
  label: string;
  provider: BaseMapProvider;
}

@Component({
  selector: 'bm-layers-panel',
  imports: [FormsModule, MatButtonModule, MatButtonToggleModule, MatIconModule, MatTooltipModule],
  templateUrl: './layers-panel.component.html',
  styleUrls: ['./layers-panel.component.scss'],
})
export class LayersPanelComponent {
  private mapService = inject(MapService);
  private detailsService = inject(FeatureLayerDetailsService);
  private externalLayersService = inject(EXTERNAL_MAP_LAYERS_SERVICE, { optional: true });

  // The tabs act purely as a view filter over one logical base-tile list;
  // they never change the map on their own.
  viewTab = signal<BaseMapProvider>(this.initialTab());

  constructor() {
    effect(() => {
      const authenticated = this.externalLayersService?.authenticated;
      if (authenticated && !authenticated()) this.viewTab.set('inventa');
    });
  }

  showEsriLogin = computed(() => {
    const service = this.externalLayersService;
    return Boolean(
      service?.available?.() !== false && service?.authenticated && !service.authenticated(),
    );
  });
  showBaseMapProviderToggle = computed(() => {
    const service = this.externalLayersService;
    return Boolean(service && service?.available?.() !== false);
  });
  showEsriLogout = computed(() => {
    const service = this.externalLayersService;
    return Boolean(service?.available?.() !== false && service?.authenticated?.());
  });
  /** Shown as the tooltip of the sign-out link: the signed-in ArcGIS account. */
  signedInAs = computed(() => {
    const username = this.externalLayersService?.username?.();
    return username ? `Signed in as ${username}` : '';
  });
  featureLayers = computed(() => this.externalLayersService?.layers() ?? []);
  canAddLayers = computed(() => {
    const service = this.externalLayersService;
    return Boolean(service?.available?.() !== false && service?.canAddLayers?.());
  });

  readonly visibleItemsLimit = 10;
  showAllFeatureLayers = signal(false);

  inventaLayers = signal<{ id: InventaLayerId; label: string }[]>([
    { id: 'speciesIcon', label: 'Species Icon' },
    { id: 'lightCondition', label: 'Light Condition' },
    { id: 'tracks', label: 'Tracks' },
    { id: 'trackArrows', label: 'Track Arrows' },
    { id: 'markers', label: 'Markers' },
    { id: 'clustering', label: 'Clustering' },
  ]);

  visibleFeatureLayers = computed(() =>
    this.showAllFeatureLayers() ? this.featureLayers() : this.featureLayers().slice(0, 10),
  );

  inventaBaseTiles: UnifiedBaseTile[] = [
    { key: 'inventa/satellite', label: 'Satellite', provider: 'inventa' },
    { key: 'inventa/hybrid', label: 'Hybrid', provider: 'inventa' },
    { key: 'inventa/roadmap', label: 'Road Map', provider: 'inventa' },
    { key: 'inventa/terrain', label: 'Terrain', provider: 'inventa' },
  ];

  // One logical list: Inventa (Google) tiles and ArcGIS tiles as siblings.
  unifiedBaseTiles = computed<UnifiedBaseTile[]>(() => {
    const esriTiles = (this.externalLayersService?.baseTileOptions() ?? []).map((tile) => ({
      ...tile,
      provider: 'esri' as const,
    }));
    return [...this.inventaBaseTiles, ...esriTiles];
  });

  visibleBaseTiles = computed<UnifiedBaseTile[]>(() =>
    this.unifiedBaseTiles().filter((tile) => tile.provider === this.viewTab()),
  );

  // Exactly one radio is checked across the whole list: the tile currently
  // shown on the map. The retained Esri preference only counts while the
  // Esri provider is active AND its basemap is actually mounted – otherwise
  // the map shows an Inventa (Google) basemap, so that radio is checked.
  activeTileKey = computed<string | undefined>(() => {
    const service = this.externalLayersService;
    if (service?.provider?.() === 'esri' && service?.activeBaseTileKey?.()) {
      return service?.activeBaseTileKey();
    }
    const mapType = this.mapTypeMapping[this.mapService.userPreferences.mapProvider];
    if (!mapType) return undefined;
    const tile = this.inventaBaseTiles.find((t) => t.key === `inventa/${mapType}`);
    return tile?.key;
  });

  // Layer states for checkboxes
  speciesIcon = this.mapService.userPreferences.mapSpeciesIconToggle;
  lightCondition = this.mapService.userPreferences.mapLightConditionIconToggle;
  tracks = this.mapService.userPreferences.showTracks;
  trackArrows = this.mapService.userPreferences.tracksArrows;
  markers = this.mapService.userPreferences.showMarkers;
  clustering = this.mapService.userPreferences.positionsClustering;
  private mapTypeMapping: Record<number, 'satellite' | 'terrain' | 'roadmap' | 'hybrid'> = {
    0: 'satellite',
    1: 'terrain',
    2: 'roadmap',
    3: 'hybrid',
  };

  private initialTab(): BaseMapProvider {
    const service = this.externalLayersService;
    // Signed in to ArcGIS: open the ArcGIS tab — right after linking an
    // account no ArcGIS tile is mounted yet (the map keeps showing the
    // Inventa basemap), but the account's layers and base tiles are what the
    // user came for. A deliberate switch back to an Inventa basemap (provider
    // inventa, esri tile retained) keeps the Inventa tab.
    if (service?.authenticated?.() && service?.provider?.() === 'esri') return 'esri';
    return 'inventa';
  }

  layerTooltip(layerName: string): string {
    if (layerName === 'markers' && this.lightCondition)
      return 'Turn off Light Condition to enable Markers';
    if (layerName === 'clustering' && !this.markers && !this.lightCondition)
      return 'Turn on Markers or Light Condition to enable Clustering';
    return '';
  }

  isLayerDisabled(layerName: string): boolean {
    if (layerName === 'trackArrows') return !this.tracks;
    if (layerName === 'markers') return this.lightCondition;
    if (layerName === 'clustering') return !this.markers && !this.lightCondition;
    return false;
  }

  onLayerToggle(layerName: InventaLayerId) {
    const isEnabled = this[layerName];
    this.mapService.toggleMapLayer(layerName, isEnabled);
  }

  onViewTabChange(tab: BaseMapProvider) {
    this.viewTab.set(tab);
  }

  onBaseTileChange(key: string) {
    const tile = this.unifiedBaseTiles().find((t) => t.key === key);
    if (!tile) return;
    if (tile.provider === 'esri') {
      this.externalLayersService?.setBasemap?.({ provider: 'esri', tile: key });
      return;
    }
    const mapType = tile.key.replace('inventa/', '') as InventaMapType;
    if (this.externalLayersService) {
      this.externalLayersService.setBasemap({ provider: 'inventa', mapType });
    } else {
      // Without the external (ArcGIS) service the Inventa tiles are the only
      // base maps; the plain Google map type still applies.
      this.mapService.setMapType(mapType);
    }
  }

  truncateLabel(label: string, maxLength = 19): string {
    return label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;
  }

  isTruncated(label: string, maxLength = 19): boolean {
    return label.length > maxLength;
  }

  onFeatureLayerToggle(key: string, event: Event) {
    this.externalLayersService?.toggle(key, (event.target as HTMLInputElement).checked);
  }

  // A name click opens the details; it also checks the layer when it is not
  // on the map yet. A checked layer is left untouched, so inspecting a legend
  // never turns the layer off.
  onFeatureLayerClick(layer: ExternalMapLayer) {
    if (!layer.checked) this.externalLayersService?.toggle(layer.key, true);
    this.detailsService.open(layer.key);
  }

  addLayers() {
    this.externalLayersService?.addLayers?.();
  }

  login() {
    this.externalLayersService?.login?.();
  }

  logout() {
    this.externalLayersService?.logout();
  }
}
